// fallow-ignore-file complexity
import {
	booleanAttribute,
	Component,
	computed,
	effect,
	forwardRef,
	input,
	isDevMode,
	output,
	signal,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import {
	normalizePreviewSwatches,
	resolveTriggerPreviewSwatches,
} from './jepto-ui-select-preview.util';

export interface SelectOption {
	value: any;
	label: string;
	/** Material Symbols ligature shown in the panel row when set. */
	icon?: string;
	/** Optional image logo shown in trigger and option rows (e.g. domain providers). */
	logoSrc?: string | null;
	/** Optional color chips shown in trigger and option rows. */
	previewSwatches?: string[];
	disabled?: boolean;
	group?: string;
}

export interface SelectGroup {
	label: string;
	options: SelectOption[];
	disabled?: boolean;
}

export interface SelectChangeEvent {
	value: any;
	option: SelectOption | null;
}

export interface SelectFocusEvent {
	value: any;
	focused: boolean;
}

export type SelectAppearance = 'fill' | 'outline';

/**
 * `mat-select` already renders a trailing chevron; these suffix ligatures only duplicate it.
 * @see JeptoUiSelectComponent.suffixIcon
 */
const REDUNDANT_SELECT_SUFFIX_ICONS = new Set([
	'expand_more',
	'keyboard_arrow_down',
	'arrow_drop_down',
	'unfold_more',
]);

@Component({
	selector: 'jepto-ui-select',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		MatIconModule,
		MatOptionModule,
		MatButtonModule,
	],
	templateUrl: './jepto-ui-select.component.html',
	styleUrl: './jepto-ui-select.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => JeptoUiSelectComponent),
			multi: true,
		},
	],
})
export class JeptoUiSelectComponent implements ControlValueAccessor {
	/** Stable id for the native select (avoids random ids every CD cycle). */
	readonly controlId = `jepto-ui-select-${Math.random().toString(36).slice(2, 11)}`;

	// Input signals
	placeholder = input('');
	label = input('');
	/** Overrides mat-select `aria-label` when the visible label differs (e.g. Answers prompt picker). */
	selectAriaLabel = input<string | undefined>();
	/** Show a help control beside the label; emits {@link labelHelpClick}. */
	labelHelpIcon = input(false, { transform: booleanAttribute });
	labelHelpAriaLabel = input('Help');
	hint = input('');
	/** Right-aligned subscript caption (e.g. range or constraint text). */
	hintEnd = input('');
	/** Trailing action label in the hint row (link or button; see {@link hintLinkHref}). */
	hintLinkLabel = input('');
	/** When set, hint link renders as `<a target="_blank">`. If empty, use {@link hintLinkClick}. */
	hintLinkHref = input('');
	errorMessage = input('');
	appearance = input<SelectAppearance>('fill');
	disabled = input(false);
	/** Replaces the trigger chevron with a spinner (e.g. while options load). */
	triggerLoading = input(false, { transform: booleanAttribute });
	required = input(false);
	multiple = input(false);
	options = input<SelectOption[]>([]);
	groups = input<SelectGroup[]>([]);
	prefixIcon = input('');
	/**
	 * Optional trailing icon (Material Symbols ligature). Do not use `expand_more` / dropdown
	 * variants — the trigger already includes the select chevron ({@link REDUNDANT_SELECT_SUFFIX_ICONS}).
	 */
	suffixIcon = input('');
	showClearButton = input(false);
	compareWith = input<(o1: any, o2: any) => boolean>((o1, o2) => o1 === o2);
	panelClass = input('');
	disableOptionCentering = input(false);
	/** Adds a search field inside the panel and filters options by label/value. */
	panelSearch = input(false, { transform: booleanAttribute });
	panelSearchPlaceholder = input('Search');
	/** When true, logs CVA / ngModel writeValue and selectionChange (dev bisect only). */
	debugNgModel = input(false, { transform: booleanAttribute });
	/** Label included in {@link debugNgModel} logs (e.g. `data-tab-granularity`). */
	debugLabel = input('');
	/** `data-testid` on the inner `mat-select` trigger. */
	testId = input<string | undefined>(undefined);
	/** `data-testid` on `mat-form-field` for overlay outside-click boundary (dialog editors). */
	fieldBoundaryTestId = input<string | undefined>(undefined);
	/**
	 * One-way value from parent (schema-field style). Prefer over `ngModel` inside
	 * `@for` + signal rows — avoids spurious `ngModelChange` / config echo loops.
	 */
	inputValue = input<any>(undefined);
	/** When true, sync display from {@link inputValue} instead of `ngModel` only. */
	useInputValue = input(false, { transform: booleanAttribute });
	/**
	 * When true, mat-select user picks still emit {@link valueChange} even when
	 * {@link compareWith} treats the value as unchanged (e.g. report theme re-pick).
	 */
	notifyOnReselect = input(false, { transform: booleanAttribute });

	readonly matSelectRef = viewChild<MatSelect>('matSelect');

	/** Exposed for parent MatDialog / overlay pointer-close wiring. */
	matSelect(): MatSelect | undefined {
		return this.matSelectRef();
	}

	// Output signals
	valueChange = output<SelectChangeEvent>();
	focusChange = output<SelectFocusEvent>();
	selectionChange = output<SelectChangeEvent>();
	openedChange = output<boolean>();
	iconClick = output<'prefix' | 'suffix'>();
	clearClick = output<void>();
	hintLinkClick = output<void>();
	labelHelpClick = output<void>();

	// Internal state
	private readonly _value = signal<any>(null);
	private _focused = false;
	/** Filter text for {@link panelSearch} (cleared on close / selection). */
	readonly panelSearchQuery = signal('');

	// ControlValueAccessor methods
	private onChange: (value: any) => void = () => {};
	private onTouched: () => void = () => {};

	constructor() {
		effect(() => {
			if (!this.useInputValue()) {
				return;
			}
			const next = this.inputValue();
			this.disabled();
			this.options();
			this.groups();
			const current = this._value();
			if (this.compareWith()(current, next)) {
				return;
			}
			// Parent explicitly clearing selection — always apply.
			if (next === null || next === '') {
				this.writeValue(next);
				return;
			}
			// Parent inputValue can lag behind a user selection still in flight.
			// If the current value matches a real option, do not overwrite it with stale input.
			if (
				current !== null &&
				current !== undefined &&
				current !== '' &&
				this.getAllOptions().some(opt =>
					this.compareWith()(opt.value, current)
				) &&
				!this.getAllOptions().some(opt =>
					this.compareWith()(opt.value, next)
				)
			) {
				return;
			}
			this.writeValue(next);
		});
	}

	// Computed properties
	showError = computed(
		() => !!(this.errorMessage() && this.errorMessage().trim())
	);
	showHint = computed(() => {
		if (this.showError()) {
			return false;
		}
		const lead = (this.hint() ?? '').trim();
		const link = (this.hintLinkLabel() ?? '').trim();
		const end = (this.hintEnd() ?? '').trim();
		return !!(lead || link || end);
	});
	hasPrefix = computed(
		() => !!(this.prefixIcon() && this.prefixIcon().trim())
	);
	/** Suffix `mat-icon` only when non‑redundant (not a duplicate of the select arrow). */
	readonly showCustomSuffixIcon = computed(() => {
		const s = (this.suffixIcon() ?? '').trim();
		if (!s) {
			return false;
		}
		if (REDUNDANT_SELECT_SUFFIX_ICONS.has(s)) {
			return false;
		}
		return true;
	});
	hasSuffix = computed(
		() => this.showCustomSuffixIcon() || this.showClearButton()
	);
	showClear = computed(() => {
		const value = this._value();
		return (
			this.showClearButton() &&
			value !== null &&
			value !== undefined &&
			!this.disabled()
		);
	});

	hasGroups = computed(() => this.groups().length > 0);
	hasOptions = computed(() => this.options().length > 0);

	readonly mergedPanelClass = computed(() => {
		const user = this.panelClass().trim();
		const base = 'jepto-ui-select__panel';
		const classes = [base];
		if (this.panelSearch()) {
			classes.push('jepto-ui-select__panel--search');
		}
		if (user) {
			classes.push(user);
		}
		return classes.join(' ');
	});

	readonly groupsForPanel = computed((): SelectGroup[] => {
		const groups = this.groups();
		if (!groups.length) {
			return [];
		}
		if (!this.panelSearch()) {
			return groups;
		}
		const q = this.panelSearchQuery().trim().toLowerCase();
		if (!q) {
			return groups;
		}
		return groups
			.map(g => ({
				...g,
				options: g.options.filter(o => this.optionMatchesQuery(o, q)),
			}))
			.filter(g => g.options.length > 0);
	});

	readonly optionsForPanel = computed((): SelectOption[] => {
		const options = this.options();
		if (!options.length) {
			return [];
		}
		if (!this.panelSearch()) {
			return options;
		}
		const q = this.panelSearchQuery().trim().toLowerCase();
		if (!q) {
			return options;
		}
		return options.filter(o => this.optionMatchesQuery(o, q));
	});

	readonly panelSearchEmpty = computed(() => {
		if (!this.panelSearch() || !this.panelSearchQuery().trim()) {
			return false;
		}
		if (this.hasGroups()) {
			return this.groupsForPanel().length === 0;
		}
		return this.optionsForPanel().length === 0;
	});

	// Get the selected option(s) for display
	selectedOption = computed(() => {
		const value = this._value();
		if (value === null || value === undefined) {
			return null;
		}

		const allOptions = this.getAllOptions();

		if (this.multiple()) {
			return Array.isArray(value)
				? value
						.map(v =>
							allOptions.find(opt =>
								this.compareWith()(opt.value, v)
							)
						)
						.filter(Boolean)
				: [];
		}

		return (
			allOptions.find(opt => this.compareWith()(opt.value, value)) || null
		);
	});

	/** Closed-field text; custom option rows must not drive the trigger (avoids icon+label concat). */
	readonly selectedDisplayLabel = computed(() => {
		if (this.multiple()) {
			const opts = this.selectedOption() as SelectOption[];
			if (!Array.isArray(opts) || opts.length === 0) {
				return this.placeholder()?.trim() || '';
			}
			return opts.map(o => o.label).join(', ');
		}
		const opt = this.selectedOption() as SelectOption | null;
		if (opt != null) {
			return opt.label ?? String(opt.value ?? '');
		}
		const value = this._value();
		if (value === null || value === undefined || value === '') {
			return this.placeholder()?.trim() || '';
		}
		return String(value);
	});

	readonly triggerIsPlaceholder = computed(() => {
		if (this.multiple()) {
			const opts = this.selectedOption() as SelectOption[];
			return (
				(!Array.isArray(opts) || opts.length === 0) &&
				!!this.placeholder()?.trim()
			);
		}
		const value = this._value();
		return (
			(value === null || value === undefined || value === '') &&
			!!this.placeholder()?.trim()
		);
	});

	readonly triggerPreviewSwatches = computed(() =>
		resolveTriggerPreviewSwatches({
			multiple: this.multiple(),
			selectedOption: this.selectedOption() as SelectOption | null,
			allOptions: this.getAllOptions(),
			value: this._value(),
		})
	);

	readonly selectedOptionLogoSrc = computed(() =>
		this.getOptionLogoSrc(this.selectedOption() as SelectOption | null)
	);

	getOptionLogoSrc(option: SelectOption | null | undefined): string | null {
		const src = option?.logoSrc;
		if (typeof src !== 'string') {
			return null;
		}
		const trimmed = src.trim();
		return trimmed && this.#isTrustedLogoSrc(trimmed) ? trimmed : null;
	}

	#isTrustedLogoSrc(src: string): boolean {
		if (src.startsWith('assets/') || src.startsWith('/assets/')) {
			return true;
		}
		try {
			return new URL(src).protocol === 'https:';
		} catch {
			return false;
		}
	}

	getPreviewSwatches(option: SelectOption | null | undefined): string[] {
		return normalizePreviewSwatches(option);
	}

	get value(): any {
		return this._value();
	}

	get focused(): boolean {
		return this._focused;
	}

	// ControlValueAccessor implementation
	writeValue(value: any): void {
		if (this.debugNgModel() && isDevMode()) {
			console.debug('[jepto-ui-select][ngModel] writeValue', {
				label: this.debugLabel() || this.label() || this.controlId,
				value,
				prev: this._value(),
			});
		}
		this._value.set(value);
		// A pick the parent rejects (inputValue unchanged) is reverted before the
		// `[value]` binding is re-checked, so mat-select would keep the rejected
		// option selected and swallow the next click on it.
		const matSelect = this.matSelectRef();
		if (matSelect && !this.compareWith()(matSelect.value, value)) {
			matSelect.value = value;
		}
	}

	registerOnChange(fn: (value: any) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		// Handled by input signal
	}

	private getAllOptions(): SelectOption[] {
		const directOptions = this.options();
		const groupOptions = this.groups().flatMap(group => group.options);
		return [...directOptions, ...groupOptions];
	}

	private optionMatchesQuery(o: SelectOption, q: string): boolean {
		const label = (o.label ?? '').toLowerCase();
		const val = String(o.value ?? '').toLowerCase();
		const icon = (o.icon ?? '').toLowerCase();
		return label.includes(q) || val.includes(q) || icon.includes(q);
	}

	onPanelSearchInput(event: Event): void {
		const t = event.target as HTMLInputElement | null;
		this.panelSearchQuery.set(t?.value ?? '');
	}

	onPanelSearchKeydown(event: KeyboardEvent): void {
		event.stopPropagation();
	}

	onOpenedChangeMerged(opened: boolean): void {
		if (!opened) {
			this.panelSearchQuery.set('');
		}
		this.openedChange.emit(opened);
	}

	onSelectionChange(value: any): void {
		if (this.panelSearch()) {
			this.panelSearchQuery.set('');
		}
		const unchanged = this.compareWith()(this._value(), value);
		if (this.debugNgModel() && isDevMode()) {
			console.debug('[jepto-ui-select][ngModel] selectionChange', {
				label: this.debugLabel() || this.label() || this.controlId,
				value,
				prev: this._value(),
				unchanged,
			});
		}
		if (!unchanged) {
			this._value.set(value);
			this.onChange(value);
		} else if (this.notifyOnReselect()) {
			this.onChange(value);
		} else {
			return;
		}

		const selectedOpt = this.selectedOption();
		const option = this.multiple()
			? Array.isArray(selectedOpt)
				? selectedOpt[selectedOpt.length - 1]
				: null
			: (selectedOpt as SelectOption);

		const changeEvent: SelectChangeEvent = {
			value,
			option,
		};

		this.valueChange.emit(changeEvent);
		this.selectionChange.emit(changeEvent);
	}

	onFocus(): void {
		this._focused = true;
		this.focusChange.emit({
			value: this._value(),
			focused: true,
		});
	}

	onBlur(): void {
		this._focused = false;
		this.onTouched();
		this.focusChange.emit({
			value: this._value(),
			focused: false,
		});
	}

	onPrefixIconClick(): void {
		this.iconClick.emit('prefix');
	}

	onSuffixIconClick(): void {
		this.iconClick.emit('suffix');
	}

	onClearClick(): void {
		if (this.panelSearch()) {
			this.panelSearchQuery.set('');
		}
		const cleared = this.multiple() ? [] : null;
		this._value.set(cleared);
		this.onChange(cleared);
		this.clearClick.emit();
		this.valueChange.emit({
			value: cleared,
			option: null,
		});
	}

	onHintLinkClick(): void {
		this.hintLinkClick.emit();
	}

	onLabelHelpClick(event: Event): void {
		event.stopPropagation();
		this.labelHelpClick.emit();
	}
}
